/**
 * Default threshold values for filtering word network links.
 * These thresholds determine which links are displayed when a specific weight type is selected.
 * 
 * @type {Object}
 * @property {number} syntacticDependency - Threshold for syntactic dependencies (Step 2: Stanford NLP SemanticGraph)
 * @property {number} semanticSimilarity - Threshold for semantic similarity (Step 3: FastText)
 * @property {number} knowledgeRelation - Threshold for ConceptNet relationships (Step 3.5: ConceptNet)
 */
export const DEFAULT_THRESHOLDS = {
    syntacticDependency: 0,
    semanticSimilarity: 0.5,
    knowledgeRelation: 0,
};

/**
 * Cytoscape layout configuration for the word network graph.
 * Uses the "cose" (Compound Spring Embedder) layout algorithm.
 */
export const LAYOUT = {
    name: "cose",
    idealEdgeLength: 150,
    nodeOverlap: 4,
    refresh: 20,
    fit: true,
    padding: 50,
    randomize: true,
    componentSpacing: 200,
    nodeRepulsion: 450000,
    edgeElasticity: 200,
    nestingFactor: 0.1,
    gravity: 0.02,
    numIter: 2500,
    initialTemp: 1000,
    coolingFactor: 0.99,
    minTemp: 1.0,
    animate: false,
    animationDuration: 0,
};

/**
 * Cytoscape style configuration for the word network graph.
 * Defines visual styling for nodes, edges, and their various states.
 */
export const STYLE = [
    {
        selector: "node",
        style: {
            "background-color": "#4a90e2",
            "label": "data(label)",
            "text-valign": "center",
            "text-halign": "center",
            "color": "#ffffff",
            "font-size": "20px",
            "font-weight": "bold",
            "width": "data(width)",
            "height": "data(height)",
            "shape": "ellipse",
            "text-wrap": "wrap",
            "text-max-width": "data(width)",
            "text-overflow-wrap": "anywhere",
            "padding": "2px",
            "border-width": 2,
            "border-color": "#2c5aa0",
        },
    },
    {
        selector: "edge",
        style: {
            "width": 2,
            "line-color": "#a0a0a0",
            "target-arrow-color": "#a0a0a0",
            "target-arrow-shape": "triangle",
            "curve-style": "bezier",
            "opacity": 0.6,
        },
    },
    {
        selector: "edge[color]",
        style: {
            "line-color": "data(color)",
            "target-arrow-color": "data(color)",
        },
    },
    {
        selector: "node:selected",
        style: {
            "background-color": "#ff6b6b",
            "border-color": "#cc5555",
            "border-width": 3,
        },
    },
    {
        selector: "edge:selected",
        style: {
            "line-color": "#ff6b6b",
            "target-arrow-color": "#ff6b6b",
            "opacity": 1,
        },
    },
    {
        selector: "node.highlighted",
        style: {
            "background-color": "#ff6b6b",
            "border-color": "#cc5555",
            "border-width": 3,
            "opacity": 1,
        },
    },
    {
        selector: "edge.highlighted[color]",
        style: {
            "line-color": "data(color)",
            "target-arrow-color": "data(color)",
            "opacity": 1,
            "width": 3,
        },
    },
    {
        selector: "node.connected",
        style: {
            "color": "#000000",
            "font-weight": "bold",
            "background-color": "#fff3cd",
            "border-color": "#ffc107",
            "border-width": 2,
        },
    },
    {
        selector: "node.dimmed",
        style: {
            "opacity": 0.2,
        },
    },
    {
        selector: "edge.dimmed",
        style: {
            "opacity": 0.1,
        },
    },
    {
        selector: "node.most-connected",
        style: {
            "background-color": "#9c27b0",
            "border-color": "#7b1fa2",
            "border-width": 4,
            "color": "#ffffff",
        },
    },
    {
        selector: "node.most-connected.highlighted",
        style: {
            "background-color": "#9c27b0",
            "border-color": "#7b1fa2",
            "border-width": 4,
            "color": "#ffffff",
        },
    },
];

